import { useEffect, useMemo, useRef } from 'react'
import { useOnce } from '@opallabs/lambda'
import { Notification } from '@opallabs/wormhole/types'
import { mapNotificationsToEntities } from './mapNotificationsToEntities'
import {
  CreateCheckpoint,
  GetNotifications,
  OnItemClick,
  OnRemoveNotification,
} from './types'
import { NotificationWrapperProps } from '.'

interface UseNotificationsPanelDataArg {
  getNotifications: GetNotifications
  createCheckpoint: CreateCheckpoint
  isLoading: boolean
  notifications: Notification[]
  onItemClick: OnItemClick
  onRemoveNotification: OnRemoveNotification
}

interface NotificationsPanelData {
  entities: NotificationWrapperProps[]
  isEmpty: boolean
}

type UseNotificationsPanelData = (
  arg: UseNotificationsPanelDataArg
) => NotificationsPanelData

export const useNotificationsPanelData: UseNotificationsPanelData = ({
  getNotifications,
  createCheckpoint,
  isLoading,
  notifications,
  onItemClick,
  onRemoveNotification,
}) => {
  useOnce(() => getNotifications({}))

  const checkpointCreated = useRef(false)

  useEffect(() => {
    if (!checkpointCreated.current && !isLoading && notifications.length > 0) {
      checkpointCreated.current = true
      createCheckpoint(notifications)
    }
  }, [checkpointCreated, createCheckpoint, isLoading, notifications])

  const isEmpty = useMemo(() => notifications.length === 0, [notifications])

  const entities = useMemo(
    () =>
      mapNotificationsToEntities({
        notifications,
        clickHandler: onItemClick,
        removeHandler: onRemoveNotification,
      }),
    [notifications, onItemClick, onRemoveNotification]
  )

  return {
    isEmpty,
    entities,
  }
}
